<template>
    <!-- Десктоп: компактный popover у точки вызова (как нативные контекстные меню) -->
    <Teleport to="body">
      <div
        v-if="isOpen && isDesktopMode"
        ref="popoverRef"
        class="modal-content conversation-popover"
        role="menu"
        :style="popoverStyle"
        @click.stop
      >
        <template v-for="(mi, idx) in menuItems" :key="mi.key">
          <div v-if="mi.danger" class="conversation-popover-divider" role="separator"></div>
          <a
            class="conversation-menu-item"
            :class="{ 'conversation-menu-item-danger': mi.danger, 'keyboard-active': idx === activeIndex }"
            href="#"
            role="menuitem"
            :aria-label="mi.label"
            @click.prevent="activateItem(mi)"
            @mouseenter="activeIndex = idx"
          >
            <i :class="mi.icon"></i>
            <span>{{ mi.label }}</span>
          </a>
        </template>
      </div>
    </Teleport>

    <!-- Мобайл: bottom sheet (без изменений) -->
    <div
      v-if="isOpen && !isDesktopMode"
      class="modal modal-bottom-sheet conversation-menu-modal fade"
      :class="{ show: isVisible }"
      tabindex="-1"
      role="dialog"
      :aria-hidden="!isVisible"
      aria-modal="true"
      @keydown.esc="close"
    >
      <div class="modal-dialog" role="document">
        <div
          class="modal-content"
          ref="contentRef"
          tabindex="-1"
          :style="dragStyle"
          @touchstart.passive="onTouchStart"
        >
          <div
            class="bottom-sheet-handle"
            aria-hidden="true"
            style="touch-action: none;"
          ></div>
          <button type="button" class="close bottom-sheet-close" @click="close" :aria-label="t('messages.close')">
            <span aria-hidden="true"></span>
          </button>

          <div class="conversation-menu-items">
            <a class="conversation-menu-item" href="#" @click.prevent="goToProfile">
              <i class="fe fe-user"></i>
              <span>{{ t('messages.go_to_profile') }}</span>
            </a>
            <a class="conversation-menu-item" href="#" @click.prevent="onGift">
              <i class="fa fa-gift"></i>
              <span>{{ t('messages.send_gift') }}</span>
            </a>
            <a class="conversation-menu-item" href="#" @click.prevent="onReport">
              <i class="fe fe-flag"></i>
              <span>{{ t('messages.report') }}</span>
            </a>
            <a class="conversation-menu-item" href="#" @click.prevent="onBlock">
              <i :class="blockButtonIcon"></i>
              <span>{{ blockButtonText }}</span>
            </a>
            <a class="conversation-menu-item" href="#" @click.prevent="onClearHistory">
              <i class="fe fe-refresh-cw"></i>
              <span>{{ t('messages.clear_history') }}</span>
            </a>
            <a class="conversation-menu-item conversation-menu-item-danger" href="#" @click.prevent="onDeleteConversation">
              <i class="fe fe-trash-2"></i>
              <span>{{ t('messages.delete_this_conversation') }}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isOpen && !isDesktopMode" class="modal-backdrop fade conversation-menu-backdrop" :class="{ show: isVisible }" @click.stop="close"></div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import { useI18n } from '@/composables/useI18n';

const { t } = useI18n();

const props = defineProps<{
  contact?: any;
  onlineStatus?: string;
  isUserBlocked?: boolean;
  blockType?: 'blocked_by_user' | 'user_blocked' | null;
}>();

const emit = defineEmits<{
  (e: 'report'): void;
  (e: 'gift'): void;
  (e: 'block'): void;
  (e: 'clearHistory'): void;
  (e: 'deleteConversation'): void;
}>();

const isOpen = ref(false);
const isVisible = ref(false);
const contentRef = ref<HTMLElement | null>(null);
const popoverRef = ref<HTMLElement | null>(null);

// Десктоп: popover у точки вызова вместо модалки-шторки
const isDesktopMode = ref(false);
const popoverX = ref(0);
const popoverY = ref(0);
const activeIndex = ref(0);
const POPOVER_WIDTH = 240;
const POPOVER_ITEM_HEIGHT = 42;

const isDesktop = () => window.matchMedia('(min-width: 768px)').matches;

const menuItems = computed(() => [
  { key: 'profile', icon: 'fe fe-user', label: t('messages.go_to_profile'), danger: false },
  { key: 'gift', icon: 'fa fa-gift', label: t('messages.send_gift'), danger: false },
  { key: 'report', icon: 'fe fe-flag', label: t('messages.report'), danger: false },
  { key: 'block', icon: blockButtonIcon.value, label: blockButtonText.value, danger: false },
  { key: 'clear', icon: 'fe fe-refresh-cw', label: t('messages.clear_history'), danger: false },
  { key: 'delete', icon: 'fe fe-trash-2', label: t('messages.delete_this_conversation'), danger: true },
]);

const popoverStyle = computed(() => ({
  left: `${popoverX.value}px`,
  top: `${popoverY.value}px`,
}));

const activateItem = (mi: { key: string }) => {
  switch (mi.key) {
    case 'profile': goToProfile(); break;
    case 'gift': onGift(); break;
    case 'report': onReport(); break;
    case 'block': onBlock(); break;
    case 'clear': onClearHistory(); break;
    case 'delete': onDeleteConversation(); break;
  }
};

// Флаг активной регистрации: без него open() через setTimeout(0) ставит
// новый click-listener ПОСЛЕ того, как close() (тот же цикл событий, всплытие
// клика) снял старые — осиротевший listener мгновенно закрывал меню при
// следующем клике (3-й и далее клики по аватарке не открывали меню)
let popoverListenersActive = false;

const onPopoverKeydown = (e: KeyboardEvent) => {
  if (!isOpen.value || !isDesktopMode.value) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    close();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % menuItems.value.length;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    activeIndex.value = (activeIndex.value - 1 + menuItems.value.length) % menuItems.value.length;
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const mi = menuItems.value[activeIndex.value];
    if (mi) activateItem(mi);
  }
};

const onDocumentClick = () => {
  if (isOpen.value && isDesktopMode.value) close();
};

const addPopoverListeners = () => {
  if (popoverListenersActive) return;
  popoverListenersActive = true;
  document.addEventListener('keydown', onPopoverKeydown);
  setTimeout(() => {
    // между кликом и таймером меню могли закрыть — не вешаем listener зря
    if (isOpen.value && isDesktopMode.value) {
      document.addEventListener('click', onDocumentClick);
    } else {
      popoverListenersActive = false;
    }
  }, 0);
};

const removePopoverListeners = () => {
  popoverListenersActive = false;
  document.removeEventListener('keydown', onPopoverKeydown);
  document.removeEventListener('click', onDocumentClick);
};

const dragY = ref(0);
const isDragging = ref(false);
let startY = 0;
let startTime = 0;
const MOBILE_CLOSE_ANIMATION_MS = 950;
const DESKTOP_CLOSE_ANIMATION_MS = 350;
const getCloseAnimationMs = () => window.innerWidth < 768 ? MOBILE_CLOSE_ANIMATION_MS : DESKTOP_CLOSE_ANIMATION_MS;
const getEmitDelayMs = () => getCloseAnimationMs() + 50;

const dragStyle = computed(() => {
  if (!isDragging.value && dragY.value === 0) return {};
  const y = Math.max(0, dragY.value);
  const opacity = Math.max(0.4, 1 - y / 400);
  return {
    transform: `translateY(${y}px)`,
    transition: isDragging.value ? 'none' : 'transform 0.3s ease, opacity 0.3s ease',
    opacity: String(opacity),
  };
});

const blockButtonText = computed(() => {
  if (props.isUserBlocked && props.blockType === 'user_blocked') {
    return t('messages.unblock_user');
  }
  return t('messages.block_user');
});

const blockButtonIcon = computed(() => {
  if (props.isUserBlocked && props.blockType === 'user_blocked') {
    return 'fe fe-unlock';
  }
  return 'fe fe-slash';
});

const goToProfile = () => {
  const url = props.contact?.url || '#';
  if (url !== '#') {
    // Устанавливаем флаг в sessionStorage для обхода проверки фото на frontend
    sessionStorage.setItem('skipPhotoVerification', 'true');
    // Добавляем параметр для обхода проверки фото на backend
    const separator = url.includes('?') ? '&' : '?';
    window.location.href = `${url}${separator}from=conversation_menu`;
  }
  close();
};

const onTouchStart = (e: TouchEvent) => {
  e.stopPropagation();
  startY = e.touches[0].clientY;
  startTime = Date.now();
  isDragging.value = true;
  dragY.value = 0;
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend', onTouchEnd);
  document.addEventListener('touchcancel', onTouchEnd);
};

const onTouchMove = (e: TouchEvent) => {
  if (!isDragging.value) return;
  e.preventDefault();
  const dy = e.touches[0].clientY - startY;
  dragY.value = Math.max(0, dy);
};

const onTouchEnd = () => {
  if (!isDragging.value) return;
  isDragging.value = false;
  document.removeEventListener('touchmove', onTouchMove);
  document.removeEventListener('touchend', onTouchEnd);
  document.removeEventListener('touchcancel', onTouchEnd);
  const elapsed = Date.now() - startTime;
  const velocity = dragY.value / Math.max(elapsed, 1);
  if (dragY.value > 70 || velocity > 0.5) {
    dragY.value = 400;
    close();
    setTimeout(() => { dragY.value = 0; }, MOBILE_CLOSE_ANIMATION_MS);
  } else {
    dragY.value = 0;
  }
};

const open = (e?: MouseEvent) => {
  // Toggle: повторный клик по аватарке закрывает меню
  if (isOpen.value) {
    close();
    return;
  }
  isDesktopMode.value = isDesktop();
  if (isDesktopMode.value) {
    // Popover: под кнопкой, прижат к правому краю кнопки, с clamp к вьюпорту
    const rect = (e?.currentTarget as HTMLElement | undefined)?.getBoundingClientRect();
    const estH = menuItems.value.length * POPOVER_ITEM_HEIGHT + 24;
    popoverX.value = Math.max(8, Math.min((rect?.right ?? window.innerWidth / 2) - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - 8));
    popoverY.value = Math.min((rect?.bottom ?? 100) + 6, window.innerHeight - estH - 8);
    activeIndex.value = 0;
    isOpen.value = true;
    addPopoverListeners();
    return;
  }
  isOpen.value = true;
  document.body.classList.add('modal-open', 'bottom-sheet-open');
  nextTick(() => {
    contentRef.value?.focus();
    requestAnimationFrame(() => {
      isVisible.value = true;
    });
  });
};

const close = () => {
  if (isDesktopMode.value) {
    isOpen.value = false;
    removePopoverListeners();
    return;
  }
  isVisible.value = false;
  document.body.classList.remove('bottom-sheet-open');
  setTimeout(() => {
    isOpen.value = false;
    document.body.classList.remove('modal-open');
  }, getCloseAnimationMs());
};

const onReport = () => {
  close();
  setTimeout(() => emit('report'), getEmitDelayMs());
};

const onGift = () => {
  close();
  setTimeout(() => emit('gift'), getEmitDelayMs());
};

const onBlock = () => {
  close();
  setTimeout(() => emit('block'), getEmitDelayMs());
};

const onClearHistory = () => {
  close();
  setTimeout(() => emit('clearHistory'), getEmitDelayMs());
};

const onDeleteConversation = () => {
  close();
  setTimeout(() => emit('deleteConversation'), getEmitDelayMs());
};

onBeforeUnmount(() => {
  document.body.classList.remove('modal-open', 'bottom-sheet-open');
  document.removeEventListener('touchmove', onTouchMove);
  document.removeEventListener('touchend', onTouchEnd);
  document.removeEventListener('touchcancel', onTouchEnd);
  removePopoverListeners();
});

defineExpose({ open, close });
</script>

<style scoped>
.conversation-menu-modal {
  display: block !important;
  z-index: 1050;
}

@media (min-width: 768px) {
  .conversation-menu-modal {
    position: absolute !important;
    inset: 0 !important;
    z-index: 10 !important;
    pointer-events: none !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .conversation-menu-backdrop {
    position: absolute !important;
    top: 57px !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 3 !important;
  }

  .conversation-menu-modal .modal-dialog {
    position: relative !important;
    margin: 0 !important;
    width: 90% !important;
    max-width: 320px !important;
    transform: scale(0.95) !important;
    opacity: 0 !important;
    z-index: 20 !important;
    pointer-events: auto !important;
    transition: transform 0.3s ease-out, opacity 0.3s ease-out !important;
  }

  .conversation-menu-modal.show .modal-dialog {
    transform: scale(1) !important;
    opacity: 1 !important;
  }

  .conversation-menu-modal .modal-content {
    border-radius: 16px;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.25);
  }

  .conversation-menu-modal .bottom-sheet-handle {
    display: none;
  }

  .conversation-menu-modal .bottom-sheet-close {
    display: block !important;
    position: absolute !important;
    top: 0.5rem !important;
    right: 0.5rem !important;
    z-index: 10 !important;
  }
}

/* Десктопный popover: компактное нативное контекстное меню */
.conversation-popover {
  position: fixed;
  z-index: 1060;
  width: 240px;
  padding: 6px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
  animation: conversation-popover-in 0.12s ease-out;
}

.conversation-popover .conversation-menu-item {
  padding: 0.5rem 0.75rem;
  max-width: none;
  border-radius: 8px;
}

.conversation-popover .conversation-menu-item i {
  font-size: 15px;
  margin-right: 10px;
}

.conversation-popover .conversation-menu-item span {
  font-size: 14px;
}

.conversation-popover .conversation-menu-item.keyboard-active {
  background-color: rgba(0, 0, 0, 0.06);
}

.conversation-popover-divider {
  height: 1px;
  margin: 4px 8px;
  background: rgba(0, 0, 0, 0.08);
}

@keyframes conversation-popover-in {
  from { opacity: 0; transform: translateY(-4px) scale(0.98); }
  to { opacity: 1; transform: none; }
}

.conversation-menu-items {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 0 0;
}

.conversation-menu-item {
  display: flex;
  align-items: center;
  padding: 0.85rem 2rem;
  color: inherit;
  text-decoration: none;
  transition: background-color 0.15s ease;
  width: 100%;
  max-width: 320px;
}

.conversation-menu-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  text-decoration: none;
  color: inherit;
}

.conversation-menu-item i {
  width: 24px;
  text-align: center;
  margin-right: 12px;
  font-size: 16px;
  opacity: 0.7;
}

.conversation-menu-item span {
  font-size: 15px;
}

.conversation-menu-item-danger {
  color: #cd201f;
}

.conversation-menu-item-danger:hover {
  color: #cd201f;
  background-color: rgba(205, 32, 31, 0.06);
}

@media (max-width: 767px) {
  .conversation-menu-modal {
    pointer-events: none !important;
  }

  .conversation-menu-modal .modal-dialog {
    pointer-events: auto !important;
  }

  .conversation-menu-modal .modal-content {
    height: auto !important;
    min-height: auto !important;
    max-height: calc(100vh - 2rem) !important;
  }

  .conversation-menu-modal .conversation-menu-items {
    flex: 0 0 auto !important;
    overflow-y: visible !important;
  }

  .conversation-menu-modal .bottom-sheet-close {
    display: none !important;
  }

  .conversation-menu-modal .bottom-sheet-handle {
    touch-action: none;
    cursor: grab;
  }

  .conversation-menu-modal .bottom-sheet-handle:active {
    cursor: grabbing;
  }

  .conversation-menu-backdrop {
    position: absolute !important;
    top: 57px !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 3 !important;
  }
}
</style>
